/// <amd-module name="VSSUI/Components/Header/Header" />

import * as React from "react";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { Breakpoint } from "VSSUI/Breakpoint";
import { HeaderCommandBar, IHeaderCommandBarFocusOptions, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { ITabProviderContext, TabProviderContext } from "VSSUI/Tabs";
import { css } from "VSSUI/Util";
import { IFocusable } from "VSSUI/Utilities/Focus";
import { CustomHeader } from "./CustomHeader";
import { IHeader, IHeaderBreakpointCommandItem, IHeaderProps } from "./Header.Props";
import { HeaderBackButton } from "./HeaderBackButton";
import { HeaderDescription } from "./HeaderDescription";
import { HeaderIcon } from "./HeaderIcon";
import { HeaderTitle } from "./HeaderTitle";
import { HeaderTitleArea } from "./HeaderTitleArea";
import { HeaderTitleRow } from "./HeaderTitleRow";

export class Header extends React.Component<IHeaderProps> implements IHeader, IFocusable<IHeaderCommandBarFocusOptions> {
    private commandBarClassName: IObservableValue<string>;
    private breakpointIndex = 0;
    private headerCommandBarRef = React.createRef<HeaderCommandBar>();

    constructor(props: Readonly<IHeaderProps>) {
        super(props);
        this.commandBarClassName = new ObservableValue(css(props.commandBarClassName, "flex-self-start", !!props.headerBreakpoints && "invisible"));
    }

    public render() {
        return (
            <TabProviderContext.Consumer>
                {(tabProviderContext: ITabProviderContext) => {
                    const {
                        backButtonProps,
                        buttonCount,
                        className,
                        contentClassName,
                        commandBarMoreButtonId,
                        description,
                        descriptionClassName,
                        descriptionId,
                        headerBreakpoints,
                        title,
                        titleAriaLevel,
                        titleClassName,
                        titleIconProps,
                        titleId,
                        titleSize,
                        useAriaLabelForOverflow
                    } = this.props;
                    const commandBarItems = this.props.commandBarItems || tabProviderContext.commandBarItems;
                    let commandBarComponent: JSX.Element | undefined = undefined;
                    if (commandBarItems) {
                        commandBarComponent = (
                            <Observer items={commandBarItems} className={this.commandBarClassName}>
                                {(props: { className: string; items: IHeaderCommandBarItem[] }) => {
                                    return (
                                        <HeaderCommandBar
                                            buttonCount={buttonCount}
                                            className={props.className}
                                            items={this.getUpdatedCommandBarItems(props.items)}
                                            moreButtonId={commandBarMoreButtonId}
                                            ref={this.headerCommandBarRef}
                                            useAriaLabelForOverflow={useAriaLabelForOverflow}
                                        />
                                    );
                                }}
                            </Observer>
                        );
                    }

                    return (
                        <>
                            {headerBreakpoints && (
                                <Breakpoint breakpoints={headerBreakpoints.map(hbp => hbp.breakpoint)} onBreakpoint={this.onBreakPoint} />
                            )}
                            <CustomHeader
                                className={css(
                                    className,
                                    commandBarItems && commandBarItems.length > 0 && "bolt-header-with-commandbar",
                                    backButtonProps && "bolt-header-with-back-button"
                                )}
                                separator={this.props.separator}
                            >
                                {backButtonProps && <HeaderBackButton buttonProps={backButtonProps} />}
                                <div className={css(contentClassName, "bolt-header-content-area flex-row flex-grow flex-self-stretch")}>
                                    {titleIconProps && <HeaderIcon iconProps={titleIconProps} titleSize={titleSize} />}
                                    <HeaderTitleArea>
                                        <HeaderTitleRow>
                                            {title && (
                                                <HeaderTitle ariaLevel={titleAriaLevel} className={titleClassName} id={titleId} titleSize={titleSize}>
                                                    {title}
                                                </HeaderTitle>
                                            )}
                                        </HeaderTitleRow>
                                        {description && (
                                            <HeaderDescription className={descriptionClassName} id={descriptionId}>
                                                {description}
                                            </HeaderDescription>
                                        )}
                                        {this.props.children}
                                    </HeaderTitleArea>
                                    {commandBarComponent}
                                </div>
                            </CustomHeader>
                        </>
                    );
                }}
            </TabProviderContext.Consumer>
        );
    }

    public focus(options: IHeaderCommandBarFocusOptions) {
        if (this.headerCommandBarRef.current) {
            this.headerCommandBarRef.current.focus(options);
        }
    }

    private onBreakPoint = (index: number, breakpoint: number) => {
        this.breakpointIndex = index;
        // This is making sure that command bar items are made visible after getting notified
        // for the exact breakpoint. Otherwise, there is flickering from default to current breakpoint.
        // If there are no breakpoints specified, actions are rendered at first place.
        this.commandBarClassName.value = css(this.props.commandBarClassName, "flex-self-start");
    };

    private getUpdatedCommandBarItems(sourceCommandBarItems: IHeaderCommandBarItem[]): IHeaderCommandBarItem[] {
        const { headerBreakpoints } = this.props;
        const breakpoint = headerBreakpoints ? headerBreakpoints[Math.max(0, this.breakpointIndex)] : null;
        if (breakpoint) {
            const { commandBarItems = [] } = breakpoint;
            // Update if there are actions changing their look for this breakpoint
            if (commandBarItems.length > 0) {
                // Convert actions to a map to access later quickly
                const commandBarItemsMap: { [id: string]: IHeaderBreakpointCommandItem } = {};
                commandBarItems.forEach(item => (commandBarItemsMap[item.id] = item));

                const targetCommandBarItems: IHeaderCommandBarItem[] = [];
                for (const commandBarItem of sourceCommandBarItems) {
                    const changedItem = commandBarItemsMap[commandBarItem.id];
                    if (changedItem) {
                        // Update command bar item with the specified properties
                        targetCommandBarItems.push({ ...commandBarItem, ...changedItem });
                    } else {
                        // If there is no change requested, use the same item
                        targetCommandBarItems.push({ ...commandBarItem });
                    }
                }

                return targetCommandBarItems;
            }
        }

        return sourceCommandBarItems;
    }
}
